import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import { Menu, MenuItem, MenuProps, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { useDataProvider, useNotify } from 'react-admin';
import { useMutation } from '@tanstack/react-query';
import React, {
  forwardRef,
  Fragment,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BaseMiniMenuProps } from './MiniMenuSelector';
import { bloxValues } from 'themes/constants';
import {
  ApiErrorResponse,
  BloxCustomDataProviderType,
  SyndicationChannelItem,
} from 'utility/types/dataProvider';
import { SimpleConfirmDeleteModal } from 'components/Modals/SimpleConfirmDeleteModal';
import {
  checkSiteAdminPermission,
  checkSyndicationFullAccessPermission,
  checkSyndicationModifyChannelsPermission,
  PermissionInfo,
} from 'utility/permissions';

import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import {
  TDeleteParams,
  TDeleteResult,
} from 'providers/contexts/extensions/functions/RequestBuilders';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useNavigate } from 'react-router';
import { useChannelsSearch } from 'screens/syndication/views/search/channels/ChannelsSearchWithContext';

export type MuiCloseEvent = Partial<Parameters<NonNullable<MenuProps['onClose']>>[0]>;
export type MuiCloseReasons = Partial<Parameters<NonNullable<MenuProps['onClose']>>[1]>;

export interface ChannelsMiniMenuConfig {
  edit?: { enabled?: boolean };
  duplicate?: { enabled?: boolean };
  delete?: { enabled?: boolean };
}

export const allChannelsMenuOptions: ChannelsMiniMenuConfig = {
  edit: { enabled: true },
  duplicate: { enabled: true },
  delete: { enabled: true },
};

// GenericSearchItem[] but in here should safely
// be able to cast SyndicationChannelItem[]
export interface ChannelsMiniMenuProps extends Omit<
  BaseMiniMenuProps,
  'selectedItems' | 'contextMenuItem'
> {
  selectedItems: SyndicationChannelItem[];
  menuConfig?: ChannelsMiniMenuConfig;
  contextMenuItem?: SyndicationChannelItem | null; // The item that was right-clicked
  isContextItemDisabled?: boolean; // True if the context item is disabled
  anchorEl?: HTMLElement | null; // For button-triggered menu (actions column)
}

type ButtonState = {
  link?: string; // href action for some buttons
  show: boolean; // option visibility based on config and user permissions
} & PermissionInfo;

export const ChannelsMiniMenu = memo(
  forwardRef<HTMLDivElement, ChannelsMiniMenuProps>(
    (
      {
        menuEvent,
        raResource,
        handleMiniMenuClose,
        clearSelectedItems,
        menuConfig = allChannelsMenuOptions,
        contextMenuItem,
        isContextItemDisabled,
        anchorEl,
      },
      ref,
    ) => {
      const theme = useTheme();
      const notify = useNotify();
      const navigate = useNavigate();
      const { t } = useTranslation();
      const { activeSite, sessionConfig } = useCosmos();
      const { queryClient } = useCosmosQueryClient();
      const { maybeDuplicateChannel } = useChannelsSearch();
      const dataProvider = useDataProvider<BloxCustomDataProviderType>();

      // Permission Checks

      const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
      const { granted: userHasFullWire } =
        checkSyndicationFullAccessPermission(sessionConfig);

      const { granted: userHasModChannelsWire, name: modifySyndiChannelsPermName } =
        checkSyndicationModifyChannelsPermission(sessionConfig);

      const actionsBtnState: PermissionInfo = useMemo(() => {
        if (userIsAdmin || userHasFullWire || userHasModChannelsWire) {
          return {
            info: undefined,
            disabled: false,
          };
        }
        return {
          info: t('blox.permissions.msg.required_short', {
            name: modifySyndiChannelsPermName,
          }),
          disabled: true,
        };
      }, [
        modifySyndiChannelsPermName,
        t,
        userHasFullWire,
        userHasModChannelsWire,
        userIsAdmin,
      ]);

      // mutation - delete a single channel by id
      const { mutateAsync: mutateDelete } = useMutation<
        TDeleteResult,
        ApiErrorResponse,
        TDeleteParams
      >({
        mutationKey: ['channelSearch', raResource],
        mutationFn: async (params: TDeleteParams) => {
          return dataProvider.delete('wire/channel', { id: params.id });
        },
        onSuccess: async (data: TDeleteResult, context: unknown) => {
          queryClient.invalidateQueries({ queryKey: [raResource] });
        },
        onError: async (error: ApiErrorResponse) => {
          notify(
            `${t('blox.general.error.label')}: ${error?.message || error?.axiosMessage}`,
            { type: 'error' },
          );
        },
      });

      // Determine menu mode: button (anchorEl) or context (menuEvent position)
      const isButtonMode = Boolean(anchorEl);

      // Minimenu UI States
      const menuX = menuEvent?.clientX ?? undefined;
      const menuY = menuEvent?.clientY ?? undefined;
      const hasXY = menuX && menuY;
      const isOpen = isButtonMode ? Boolean(anchorEl) : Boolean(hasXY);
      const anchorPos: MenuProps['anchorPosition'] = hasXY
        ? { top: menuY, left: menuX }
        : undefined;

      // For channels, we only delete one at a time (the right-clicked item)
      const channelItem = contextMenuItem;
      const itemId = channelItem?.id;

      // --------------------------------------------------
      // Event Handlers, Styles
      // --------------------------------------------------
      const iconSx: SxProps<Theme> = useMemo(() => {
        return {
          fontSize: bloxValues.iconSizeRegular,
          color: theme.palette.text.secondary,
          mr: 1,
        };
      }, [theme]);

      // for subsequent right clicks on background items
      // while menu open we will do some tricks hiding backdrop,
      // pass pointer events through, removing transitions
      // and add a few capture listeners to handle event/close
      // also tried this with <Popper> as well but the scroll locking
      // feature that menu has felt better than without it
      const menuSx: SxProps<Theme> = useMemo(() => {
        return {
          '& .MuiMenuItem-root': {
            fontSize: bloxValues.regularFontSizeRem,
          },
        };
      }, []);

      const menuListRef = useRef<HTMLUListElement>(null);

      const internalOnClose = useCallback(
        (event?: MuiCloseEvent, reason?: MuiCloseReasons) => {
          // Stop propagation to prevent click-away from bubbling
          // to the row link wrapper and triggering navigation
          if (event && 'stopPropagation' in event) {
            (event as Event).stopPropagation();
            (event as Event).preventDefault();
          }
          handleMiniMenuClose();
        },
        [handleMiniMenuClose],
      );

      const handleMenuClickCaptures = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
          // if click is outside the menu prevent bubbling/default
          // and then forward to internalOnClose to handle close
          if (!menuListRef?.current?.contains(e?.target as Node)) {
            e.stopPropagation();
            e.preventDefault();
            internalOnClose(e, 'backdropClick');
            return;
          }
          // else - the click happened inside the menu
          // so let event pass to menu click handlers
          // and have them handle event as needed
        },
        [internalOnClose],
      );

      // --------------------------------------------------
      // Edit Button
      // --------------------------------------------------

      const editState: ButtonState = useMemo(() => {
        const showEdit = Boolean(menuConfig?.edit?.enabled);
        return {
          disabled: false,
          link: `/${activeSite}/feeds/channels/edit/${itemId}/`,
          show: showEdit,
        };
      }, [activeSite, menuConfig?.edit?.enabled, itemId]);

      const handleOpenEdit = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          if (editState?.disabled || !editState?.link) return;
          navigate(editState.link);
        },
        [editState, navigate],
      );

      // --------------------------------------------------
      // Duplicate Button
      // --------------------------------------------------

      const duplicateState: ButtonState = useMemo(() => {
        const showDuplicate = Boolean(menuConfig?.duplicate?.enabled);
        return {
          disabled: isContextItemDisabled || !channelItem || !itemId,
          show: showDuplicate,
        };
      }, [isContextItemDisabled, menuConfig?.duplicate?.enabled, channelItem, itemId]);

      const handleDuplicate = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          if (duplicateState?.disabled) return;
          maybeDuplicateChannel({ id: itemId });
        },
        [duplicateState, itemId, maybeDuplicateChannel],
      );

      // --------------------------------------------------
      // Delete Button
      // Channels can only be deleted one at a time
      // --------------------------------------------------

      const deleteBtnState: ButtonState = useMemo(() => {
        const showDelete = Boolean(menuConfig?.delete?.enabled);

        return {
          disabled: isContextItemDisabled || !channelItem || !itemId,
          show: showDelete,
        };
      }, [isContextItemDisabled, menuConfig?.delete?.enabled, channelItem, itemId]);

      const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

      const openDeleteDialog = useCallback(() => {
        setConfirmDeleteOpen(true);
        internalOnClose();
      }, [internalOnClose]);

      const closeDeleteDialog = useCallback(() => {
        setConfirmDeleteOpen(false);
      }, []);

      // confirm delete action in dialog
      const handleDeleteConfirm = useCallback(async () => {
        if (!itemId) {
          console.error('[handleDeleteConfirm] No channel id to delete.');
          closeDeleteDialog();
          return;
        }
        // delete the saved search
        try {
          await mutateDelete({ id: itemId });
          notify(t('blox.notifier.channel.channel_deleted'), { type: 'success' });
        } catch (e) {
          console.error('[handleDeleteConfirm] Error:', e);
        } finally {
          clearSelectedItems();
          closeDeleteDialog();
        }
      }, [t, notify, itemId, mutateDelete, clearSelectedItems, closeDeleteDialog]);

      // cancel delete action in dialog
      const handleDeleteCancel = useCallback(() => {
        closeDeleteDialog();
      }, [closeDeleteDialog]);

      // open delete dialog from minimenu
      const handleDeleteMenuClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          if (deleteBtnState?.disabled) return;
          openDeleteDialog();
        },
        [deleteBtnState?.disabled, openDeleteDialog],
      );

      if (actionsBtnState.disabled) {
        return null; // if no buttons to show, render nothing
      }

      return (
        <Fragment>
          <SimpleConfirmDeleteModal
            open={confirmDeleteOpen}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            subTxt={''}
            initialWidth={350}
            initialHeight={215}
            isDraggable={true}
          />
          <Menu
            ref={ref}
            data-testid="channels-minimenu"
            disableEnforceFocus={true}
            open={isOpen}
            onClose={internalOnClose}
            sx={menuSx}
            hideBackdrop={true}
            onMouseDownCapture={handleMenuClickCaptures}
            onContextMenuCapture={handleMenuClickCaptures}
            {...(isButtonMode
              ? { anchorEl }
              : {
                  anchorReference: 'anchorPosition' as const,
                  anchorPosition: anchorPos,
                  TransitionProps: { timeout: { enter: 0, exit: 0 } },
                })}
            slotProps={{
              list: {
                autoFocusItem: true,
                ref: menuListRef,
              },
            }}
          >
            {editState?.show ? (
              <MenuItem
                data-testid="quickfilter-minimenu-edit"
                disabled={editState?.disabled}
                onClick={handleOpenEdit}
              >
                <EditIcon sx={iconSx} />
                {t('blox.actions.edit')}
              </MenuItem>
            ) : null}
            {duplicateState?.show ? (
              <MenuItem
                data-testid="channels-minimenu-duplicate"
                disabled={duplicateState?.disabled}
                onClick={handleDuplicate}
              >
                <CopyAllIcon sx={iconSx} />
                {t('blox.actions.duplicate')}
              </MenuItem>
            ) : null}
            {deleteBtnState?.show ? (
              <MenuItem
                data-testid="channels-minimenu-delete"
                onClick={handleDeleteMenuClick}
                disabled={deleteBtnState?.disabled}
              >
                <DeleteForeverIcon sx={iconSx} />
                {t('blox.actions.delete')}
              </MenuItem>
            ) : null}
          </Menu>
        </Fragment>
      );
    },
  ),
);

ChannelsMiniMenu.displayName = 'ChannelsMiniMenu';
